import type { PropertyValues } from 'lit'
import type { OverflowCollapseFrom } from './_partition.js'
import { html, isServer, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'

import { NordEvent } from '../common/events.js'
import { injectLightDomStyles, setLightDomScope } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { partitionOverflowItems } from './_partition.js'
import style from './OverflowList.light-dom.css'

export type { OverflowCollapseFrom } from './_partition.js'

export type OverflowListGap = 'none' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
export type OverflowListObserve = 'parent' | 'self'

export interface OverflowListItem {
  readonly element: HTMLElement
  readonly index: number
}

function snapshotItems(
  items: readonly OverflowListItem[],
): readonly OverflowListItem[] {
  return Object.freeze(
    items.map(({ element, index }) => Object.freeze({ element, index })),
  )
}

/**
 * Event dispatched after the set of visible or overflowed items changes.
 * Both arrays retain DOM order and expose each element's original item index.
 */
export class OverflowListChangeEvent extends NordEvent {
  static eventName = 'nord-overflow-change'

  declare readonly visibleItems: readonly OverflowListItem[]
  declare readonly overflowItems: readonly OverflowListItem[]

  constructor(
    visibleItems: readonly OverflowListItem[],
    overflowItems: readonly OverflowListItem[],
  ) {
    super(OverflowListChangeEvent.eventName)
    Object.defineProperties(this, {
      visibleItems: {
        value: snapshotItems(visibleItems),
        enumerable: true,
        writable: false,
      },
      overflowItems: {
        value: snapshotItems(overflowItems),
        enumerable: true,
        writable: false,
      },
    })
  }
}

interface OwnedVisibility {
  inert?: string
  hidden?: string
}

interface MeasuredElement {
  element: HTMLElement
  inlineSize: number
}

type InlineAxis = 'horizontal' | 'vertical'

const scopeKey = 'nord-overflow-list'
const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'nord-button:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable]:not([contenteditable="false"])',
].join(',')

/**
 * Overflow List shows the contiguous set of direct child items that fits in
 * the available inline size and exposes the remaining items to a consumer-owned
 * overflow affordance.
 *
 * @status draft
 * @category layout
 * @lightDom true
 *
 * @slot - Ordered items managed by the Overflow List.
 * @slot overflow - Consumer-owned affordance for access to overflowed content.
 *
 * @fires {OverflowListChangeEvent} nord-overflow-change - Dispatched when the visible or overflowed item set changes. The event exposes immutable `visibleItems` and `overflowItems` arrays containing each element and its original index.
 *
 * @cssprop [--n-overflow-list-gap=var(--n-space-s)] - Controls logical spacing between visible items and supplied overflow content.
 */
@customElement('nord-overflow-list')
export default class OverflowList extends DraftComponentMixin(LitElement) {
  /** Logical edge from which items collapse. */
  @property({ attribute: 'collapse-from', reflect: true })
  collapseFrom: OverflowCollapseFrom = 'end'

  /** Nord spacing token used between items. */
  @property({ reflect: true })
  gap: OverflowListGap = 's'

  /** Minimum number of items that remain visible, even when they do not fit. */
  @property({ attribute: 'min-visible-items', reflect: true, type: Number })
  minVisibleItems = 0

  /** Element whose available inline size is observed. */
  @property({ reflect: true })
  observe: OverflowListObserve = 'self'

  private readonly measurementRef = createRef<HTMLSpanElement>()
  private readonly ownedVisibility = new Map<HTMLElement, OwnedVisibility>()
  private _visibleItems: readonly OverflowListItem[] = Object.freeze([])
  private _overflowItems: readonly OverflowListItem[] = Object.freeze([])
  private resizeObserver?: ResizeObserver
  private mutationObserver?: MutationObserver
  private directionObserver?: MutationObserver
  private resizeTargets: readonly Element[] = []
  private observedInlineSizeTarget?: Element
  private observedContentBoxInlineSize?: number
  private observedContentBoxBlockSize?: number
  private observedContentBoxAxis?: InlineAxis
  private measuredAffordances: readonly HTMLElement[] = []
  private measuredAffordanceAxis?: InlineAxis
  private measuredAffordanceGap?: number
  private maximumOverflowInlineSize = 0
  private partitionChangedLastMeasurement = false
  private animationFrame?: number
  private fontSet?: FontFaceSet
  private connectionGeneration = 0

  /** Current visible elements and their original direct-item indices. */
  get visibleItems(): readonly OverflowListItem[] {
    return this._visibleItems
  }

  /** Current overflowed elements and their original direct-item indices. */
  get overflowItems(): readonly OverflowListItem[] {
    return this._overflowItems
  }

  /** Number of currently overflowed items. */
  get overflowCount(): number {
    return this._overflowItems.length
  }

  connectedCallback() {
    super.connectedCallback()
    injectLightDomStyles(this, style, scopeKey)
    this.setAttribute('data-n-overflow-list-measuring', '')

    if (isServer) {
      return
    }

    this.connectionGeneration++
    this.setupObservers()
    this.scheduleMeasurement()
  }

  disconnectedCallback() {
    this.connectionGeneration++
    this.resizeObserver?.disconnect()
    this.resizeObserver = undefined
    this.resizeTargets = []
    this.observedInlineSizeTarget = undefined
    this.observedContentBoxInlineSize = undefined
    this.observedContentBoxBlockSize = undefined
    this.observedContentBoxAxis = undefined
    this.measuredAffordances = []
    this.measuredAffordanceAxis = undefined
    this.measuredAffordanceGap = undefined
    this.maximumOverflowInlineSize = 0
    this.partitionChangedLastMeasurement = false
    this.mutationObserver?.disconnect()
    this.mutationObserver = undefined
    this.directionObserver?.disconnect()
    this.directionObserver = undefined
    this.fontSet?.removeEventListener('loadingdone', this.handleFontLoad)
    this.fontSet?.removeEventListener('loadingerror', this.handleFontLoad)
    this.fontSet = undefined
    if (this.animationFrame !== undefined) {
      cancelAnimationFrame(this.animationFrame)
      this.animationFrame = undefined
    }
    this.restoreOwnedVisibility()
    this.removeAttribute('data-n-overflow-list-measuring')
    super.disconnectedCallback()
  }

  protected createRenderRoot() {
    return this
  }

  protected firstUpdated() {
    this.scopeOwnedElements()
    this.scheduleMeasurement()
  }

  protected updated(changedProperties: PropertyValues<this>) {
    this.scopeOwnedElements()
    if (
      changedProperties.has('collapseFrom')
      || changedProperties.has('gap')
      || changedProperties.has('minVisibleItems')
      || changedProperties.has('observe')
    ) {
      this.scheduleMeasurement()
    }
  }

  private scopeOwnedElements() {
    const measurement = this.measurementRef.value
    if (measurement) {
      setLightDomScope(scopeKey, measurement)
    }
  }

  private setupObservers() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = typeof ResizeObserver === 'undefined'
      ? undefined
      : new ResizeObserver((entries) => {
          const entry = entries.find(({ target }) => target === this.observedInlineSizeTarget)
          const contentBoxSize = entry && (Array.isArray(entry.contentBoxSize)
            ? entry.contentBoxSize[0]
            : entry.contentBoxSize as unknown as ResizeObserverSize)
          if (entry && contentBoxSize && Number.isFinite(contentBoxSize.inlineSize)) {
            this.observedContentBoxInlineSize = contentBoxSize.inlineSize
            this.observedContentBoxBlockSize = Number.isFinite(contentBoxSize.blockSize)
              ? contentBoxSize.blockSize
              : undefined
            this.observedContentBoxAxis = this.inlineAxis(entry.target)
          }
          if (
            !this.partitionChangedLastMeasurement
            && entries.some(({ target }) => this.measuredAffordances.includes(target as HTMLElement))
          ) {
            this.maximumOverflowInlineSize = 0
          }
          this.scheduleMeasurement()
        })

    this.mutationObserver?.disconnect()
    this.mutationObserver = new MutationObserver((records) => {
      if (
        !this.partitionChangedLastMeasurement
        && records.some(record => this.measuredAffordances.some(
          element => record.target === element || element.contains(record.target),
        ))
      ) {
        this.maximumOverflowInlineSize = 0
      }
      this.scheduleMeasurement()
    })
    this.observeMutations()

    this.directionObserver?.disconnect()
    this.directionObserver = new MutationObserver(() => this.scheduleMeasurement())
    this.directionObserver.observe(this.ownerDocument.documentElement, {
      attributes: true,
      attributeFilter: ['dir'],
      subtree: true,
    })

    this.fontSet = this.ownerDocument.fonts
    this.fontSet?.addEventListener('loadingdone', this.handleFontLoad)
    this.fontSet?.addEventListener('loadingerror', this.handleFontLoad)
    const generation = this.connectionGeneration
    void this.fontSet?.ready.then(() => {
      if (this.isConnected && generation === this.connectionGeneration) {
        this.scheduleMeasurement()
      }
    })
  }

  private observeMutations() {
    this.mutationObserver?.observe(this, {
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    })
  }

  private handleFontLoad = () => this.scheduleMeasurement()

  private scheduleMeasurement = () => {
    if (!this.isConnected || this.animationFrame !== undefined) {
      return
    }
    this.animationFrame = requestAnimationFrame(() => {
      this.animationFrame = undefined
      this.measure()
    })
  }

  private syncResizeTargets(targets: readonly Element[]) {
    if (!this.resizeObserver) {
      return
    }
    const observedTarget = targets[0]
    if (observedTarget !== this.observedInlineSizeTarget) {
      this.observedInlineSizeTarget = observedTarget
      this.observedContentBoxInlineSize = undefined
      this.observedContentBoxBlockSize = undefined
      this.observedContentBoxAxis = undefined
    }
    if (
      targets.length === this.resizeTargets.length
      && targets.every((target, index) => target === this.resizeTargets[index])
    ) {
      return
    }

    this.resizeObserver.disconnect()
    targets.forEach(target => this.resizeObserver?.observe(target))
    this.resizeTargets = targets
  }

  private restoreOwnedVisibility() {
    for (const [element, owned] of this.ownedVisibility) {
      if (owned.hidden !== undefined && element.getAttribute('hidden') === owned.hidden) {
        element.removeAttribute('hidden')
      }
      if (owned.inert !== undefined && element.getAttribute('inert') === owned.inert) {
        element.removeAttribute('inert')
      }
    }
    this.ownedVisibility.clear()
  }

  private hide(element: HTMLElement) {
    const owned: OwnedVisibility = {
      hidden: element.hasAttribute('hidden') ? undefined : '',
      inert: element.hasAttribute('inert') ? undefined : '',
    }
    if (owned.hidden !== undefined) {
      element.setAttribute('hidden', owned.hidden)
    }
    if (owned.inert !== undefined) {
      element.setAttribute('inert', owned.inert)
    }
    if (owned.hidden !== undefined || owned.inert !== undefined) {
      this.ownedVisibility.set(element, owned)
    }
  }

  private itemElements(): HTMLElement[] {
    const measurement = this.measurementRef.value
    return Array.from(this.children).filter((element): element is HTMLElement => (
      element instanceof HTMLElement
      && element !== measurement
      && element.slot !== 'overflow'
    ))
  }

  private overflowElements(): HTMLElement[] {
    const measurement = this.measurementRef.value
    return Array.from(this.children).filter((element): element is HTMLElement => (
      element instanceof HTMLElement
      && element !== measurement
      && element.slot === 'overflow'
    ))
  }

  private inlineAxis(element: Element): InlineAxis {
    return /^(?:vertical|sideways)-/.test(getComputedStyle(element).writingMode)
      ? 'vertical'
      : 'horizontal'
  }

  private effectiveZoom(element: Element): number {
    if ('currentCSSZoom' in element) {
      const currentCSSZoom = element.currentCSSZoom
      if (
        typeof currentCSSZoom === 'number'
        && Number.isFinite(currentCSSZoom)
        && currentCSSZoom > 0
      ) {
        return currentCSSZoom
      }
    }

    let zoom = 1
    let current: Element | null = element
    while (current) {
      const serializedZoom = getComputedStyle(current).getPropertyValue('zoom').trim()
      const parsedZoom = Number.parseFloat(serializedZoom)
      const currentZoom = serializedZoom.endsWith('%') ? parsedZoom / 100 : parsedZoom
      if (Number.isFinite(currentZoom) && currentZoom > 0) {
        zoom *= currentZoom
      }
      const root = current.getRootNode()
      current = current.assignedSlot
        ?? current.parentElement
        ?? (root instanceof ShadowRoot ? root.host : null)
    }
    return zoom
  }

  private borderBoxSizeOnAxis(
    element: HTMLElement,
    axis: InlineAxis,
    hostZoom: number,
  ): number {
    const rect = element.getBoundingClientRect()
    return (axis === 'vertical' ? rect.height : rect.width) / hostZoom
  }

  private elementInlineSize(
    element: HTMLElement,
    axis: InlineAxis,
    hostZoom: number,
  ): number {
    const computed = getComputedStyle(element)
    const marginStart = Number.parseFloat(
      axis === 'vertical' ? computed.marginTop : computed.marginLeft,
    ) || 0
    const marginEnd = Number.parseFloat(
      axis === 'vertical' ? computed.marginBottom : computed.marginRight,
    ) || 0
    const marginScale = this.effectiveZoom(element) / hostZoom
    return this.borderBoxSizeOnAxis(element, axis, hostZoom)
      + (marginStart + marginEnd) * marginScale
  }

  private availableInlineSize(axis: InlineAxis, hostZoom: number): number {
    const target = this.observe === 'parent' ? this.parentElement ?? this : this
    const computed = getComputedStyle(target)
    const targetAxis = this.inlineAxis(target)
    const targetScale = this.effectiveZoom(target) / hostZoom
    const observedContentBoxSize = axis === targetAxis
      ? this.observedContentBoxInlineSize
      : this.observedContentBoxBlockSize
    if (
      target === this.observedInlineSizeTarget
      && targetAxis === this.observedContentBoxAxis
      && observedContentBoxSize !== undefined
    ) {
      return observedContentBoxSize * targetScale
    }

    const paddingStart = Number.parseFloat(
      axis === 'vertical' ? computed.paddingTop : computed.paddingLeft,
    ) || 0
    const paddingEnd = Number.parseFloat(
      axis === 'vertical' ? computed.paddingBottom : computed.paddingRight,
    ) || 0
    const clientInlineSize = axis === 'vertical' ? target.clientHeight : target.clientWidth
    return Math.max(0, clientInlineSize - paddingStart - paddingEnd) * targetScale
  }

  private gapInlineSize(axis: InlineAxis, hostZoom: number): number {
    const measurement = this.measurementRef.value
    return measurement ? this.borderBoxSizeOnAxis(measurement, axis, hostZoom) : 0
  }

  private overflowInlineSize(
    elements: readonly HTMLElement[],
    gap: number,
    axis: InlineAxis,
    hostZoom: number,
  ): number | undefined {
    if (elements.length === 0) {
      this.measuredAffordances = []
      this.measuredAffordanceAxis = undefined
      this.measuredAffordanceGap = undefined
      this.maximumOverflowInlineSize = 0
      return undefined
    }

    const affordanceChanged = elements.length !== this.measuredAffordances.length
      || elements.some((element, index) => element !== this.measuredAffordances[index])
      || axis !== this.measuredAffordanceAxis
      || gap !== this.measuredAffordanceGap
    if (affordanceChanged) {
      this.measuredAffordances = [...elements]
      this.measuredAffordanceAxis = axis
      this.measuredAffordanceGap = gap
      this.maximumOverflowInlineSize = 0
    }

    const measuredInlineSize = elements.reduce(
      (sum, element) => sum + this.elementInlineSize(element, axis, hostZoom),
      gap * Math.max(0, elements.length - 1),
    )
    this.maximumOverflowInlineSize = Math.max(
      this.maximumOverflowInlineSize,
      measuredInlineSize,
    )
    return this.maximumOverflowInlineSize
  }

  private publicItems(
    entries: readonly { item: MeasuredElement, index: number }[],
  ): readonly OverflowListItem[] {
    return Object.freeze(entries.map(({ item, index }) => Object.freeze({
      element: item.element,
      index,
    })))
  }

  private sameItems(
    current: readonly OverflowListItem[],
    next: readonly OverflowListItem[],
  ): boolean {
    return current.length === next.length && current.every((item, index) => (
      item.element === next[index].element && item.index === next[index].index
    ))
  }

  private deepActiveElement(): HTMLElement | undefined {
    const root = this.getRootNode()
    let activeElement = root instanceof Document || root instanceof ShadowRoot
      ? root.activeElement
      : undefined
    while (activeElement instanceof HTMLElement && activeElement.shadowRoot?.activeElement) {
      activeElement = activeElement.shadowRoot.activeElement
    }
    return activeElement instanceof HTMLElement ? activeElement : undefined
  }

  private composedContains(container: HTMLElement, element: HTMLElement): boolean {
    let current: Node | null = element
    while (current) {
      if (current === container) {
        return true
      }
      const root = current.getRootNode()
      current = current.parentNode ?? (root instanceof ShadowRoot ? root.host : null)
    }
    return false
  }

  private moveFocusFromHiddenItems(
    activeElement: HTMLElement | undefined,
    overflowItems: readonly OverflowListItem[],
    affordances: readonly HTMLElement[],
  ) {
    if (!activeElement) {
      return
    }
    if (!overflowItems.some(({ element }) => this.composedContains(element, activeElement))) {
      return
    }

    const affordance = affordances.find(element => !element.hidden && !element.inert)
    if (affordance) {
      const candidates = [
        affordance,
        ...affordance.querySelectorAll<HTMLElement>(focusableSelector),
      ]
      for (const candidate of candidates) {
        candidate.focus()
        const nextActiveElement = this.deepActiveElement()
        if (nextActiveElement && this.composedContains(affordance, nextActiveElement)) {
          return
        }
      }
    }

    activeElement.blur()
  }

  private moveFocusFromHiddenAffordances(
    activeElement: HTMLElement | undefined,
    affordances: readonly HTMLElement[],
    visibleItems: readonly OverflowListItem[],
  ) {
    if (!activeElement) {
      return
    }
    if (!affordances.some(element => this.composedContains(element, activeElement))) {
      return
    }

    const edgeItem = this.collapseFrom === 'start'
      ? visibleItems[0]?.element
      : visibleItems.at(-1)?.element
    if (edgeItem) {
      const candidates = [
        edgeItem,
        ...edgeItem.querySelectorAll<HTMLElement>(focusableSelector),
      ]
      for (const candidate of candidates) {
        candidate.focus()
        const nextActiveElement = this.deepActiveElement()
        if (nextActiveElement && this.composedContains(edgeItem, nextActiveElement)) {
          return
        }
      }
    }

    activeElement.blur()
  }

  private measure() {
    if (!this.isConnected || !this.measurementRef.value) {
      return
    }

    this.mutationObserver?.disconnect()
    this.restoreOwnedVisibility()

    const itemElements = this.itemElements()
    const overflowElements = this.overflowElements()
    const observedTarget = this.observe === 'parent' ? this.parentElement ?? this : this
    this.syncResizeTargets([observedTarget, ...itemElements, ...overflowElements])

    const axis = this.inlineAxis(this)
    const hostZoom = this.effectiveZoom(this)
    const gap = this.gapInlineSize(axis, hostZoom)
    const measuredItems: MeasuredElement[] = itemElements.map(element => ({
      element,
      inlineSize: this.elementInlineSize(element, axis, hostZoom),
    }))
    const overflowInlineSize = this.overflowInlineSize(
      overflowElements,
      gap,
      axis,
      hostZoom,
    )
    const partition = partitionOverflowItems(measuredItems, {
      availableInlineSize: this.availableInlineSize(axis, hostZoom),
      collapseFrom: this.collapseFrom,
      gap,
      minVisibleItems: this.minVisibleItems,
      overflowInlineSize,
    })
    const visibleItems = this.publicItems(partition.visibleItems)
    const overflowItems = this.publicItems(partition.overflowItems)
    const activeElement = this.deepActiveElement()

    if (overflowItems.length === 0) {
      this.moveFocusFromHiddenAffordances(activeElement, overflowElements, visibleItems)
      overflowElements.forEach(element => this.hide(element))
    }
    else {
      this.moveFocusFromHiddenItems(activeElement, overflowItems, overflowElements)
      overflowItems.forEach(({ element }) => this.hide(element))
    }

    const changed = !this.sameItems(this._visibleItems, visibleItems)
      || !this.sameItems(this._overflowItems, overflowItems)
    this.partitionChangedLastMeasurement = changed
    this._visibleItems = visibleItems
    this._overflowItems = overflowItems
    this.dataset.overflowCount = String(overflowItems.length)
    this.removeAttribute('data-n-overflow-list-measuring')
    this.observeMutations()

    if (changed) {
      this.dispatchEvent(new OverflowListChangeEvent(visibleItems, overflowItems))
      this.scheduleMeasurement()
    }
  }

  render() {
    return html`
      <span
        ${ref(this.measurementRef)}
        class="n-overflow-list-measurement"
        data-n-overflow-list-owned="measurement"
        aria-hidden="true"
        inert
      ></span>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-overflow-list': OverflowList
  }
  interface GlobalEventHandlersEventMap {
    'nord-overflow-change': OverflowListChangeEvent
  }
  interface HTMLElementEventMap {
    'nord-overflow-change': OverflowListChangeEvent
  }
  interface DocumentEventMap {
    'nord-overflow-change': OverflowListChangeEvent
  }
  interface WindowEventMap {
    'nord-overflow-change': OverflowListChangeEvent
  }
}
